import React, { useState, useEffect } from 'react';
import { 
  Search, 
  X, 
  BookOpen, 
  Building, 
  Shield, 
  Plus, 
  ChevronRight, 
  Bot, 
  Clock, 
  MessageSquare, 
  Lightbulb,
  Play,
  RefreshCw,
  Eye,
  CheckSquare,
  FileVideo
} from 'lucide-react';
import type { Perfil, Topico, Subtopico } from '../types';
import { getProfileMetrics, getProfileCategory, getCategoryIcon } from './EcadCatalogDashboard';
import { api } from '../../../../api/client';
import { EcadProcessSidebar } from './EcadProcessSidebar';

interface EcadPerfilSummaryProps {
  selectedPerfil: Perfil | undefined;
  selectedPerfilId: string | null;
  searchQuery: string;
  setSearchQuery: (val: string) => void;
  setSelectedTopicoId: (id: string | null) => void;
  setSelectedSubtopicoId: (id: string | null) => void;
  setTopicoForm: (form: any) => void;
  setShowTopicoModal: (val: boolean) => void;
  onTriggerAction?: (action: string, detail?: string) => void;

  globalProcStatus?: 'idle' | 'running' | 'stopping' | 'stopped';
  setGlobalProcStatus?: React.Dispatch<React.SetStateAction<'idle' | 'running' | 'stopping' | 'stopped'>>;
  globalProcProgress?: number;
  setGlobalProcProgress?: React.Dispatch<React.SetStateAction<number>>;
  globalProcTimer?: number;
  setGlobalProcTimer?: React.Dispatch<React.SetStateAction<number>>;
  globalProcLogs?: string[];
  setGlobalProcLogs?: React.Dispatch<React.SetStateAction<string[]>>;
  globalAutoShutdown?: boolean;
  setGlobalAutoShutdown?: React.Dispatch<React.SetStateAction<boolean>>;
  completedRoteiroIds?: string[];
  queuedRoteiros?: Subtopico[];
  activeRoteiro?: Subtopico | null;
}

interface KpiCardProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  color: string;
  glowColor: string;
}

function KpiCard({ icon, label, value, color, glowColor }: KpiCardProps) {
  const isZero = value === 0 || value === '0';
  return (
    <div style={{
      background: isZero ? '#e2e8f0' : color,
      border: '1px solid transparent',
      borderRadius: '12px',
      padding: '12px 14px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      boxShadow: isZero ? 'none' : `0 4px 14px ${glowColor}`,
      opacity: isZero ? 0.45 : 1,
      transition: 'all 0.2s',
      flex: 1,
      minWidth: '130px',
      color: isZero ? '#64748b' : '#ffffff'
    }}>
      <div style={{ color: isZero ? '#94a3b8' : '#ffffff', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
        {icon}
      </div>
      <div>
        <div style={{ fontSize: '0.62rem', color: isZero ? '#94a3b8' : 'rgba(255, 255, 255, 0.8)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          {label}
        </div>
        <div style={{ fontSize: '1.2rem', fontWeight: 900, color: isZero ? '#64748b' : '#ffffff', lineHeight: 1.1 }}>
          {value}
        </div>
      </div>
    </div>
  );
}

export function EcadPerfilSummary({
  selectedPerfil,
  selectedPerfilId,
  searchQuery,
  setSearchQuery,
  setSelectedTopicoId,
  setSelectedSubtopicoId,
  setTopicoForm,
  setShowTopicoModal,
  onTriggerAction,
  globalProcStatus = 'idle',
  setGlobalProcStatus = () => {},
  globalProcProgress = 0,
  setGlobalProcProgress = () => {},
  globalProcTimer = 0,
  setGlobalProcTimer = () => {},
  globalProcLogs = [],
  setGlobalProcLogs = () => {},
  globalAutoShutdown = true,
  setGlobalAutoShutdown = () => {},
  completedRoteiroIds = [],
  queuedRoteiros = [],
  activeRoteiro = null,
}: EcadPerfilSummaryProps) {
  const [escolasCount, setEscolasCount] = useState<number>(0);

  useEffect(() => {
    api.get('/escolas')
      .then(res => {
        if (Array.isArray(res.data)) {
          setEscolasCount(res.data.length);
        } else if (res.data && typeof res.data.total === 'number') {
          setEscolasCount(res.data.total);
        } else {
          setEscolasCount(8);
        }
      })
      .catch(() => {
        setEscolasCount(8);
      });
  }, []);

  const metrics = React.useMemo(() => {
    if (!selectedPerfil) return { pendentes: 0, feedbacks: 0, sugestoes: 0 };
    return getProfileMetrics(selectedPerfil.nome);
  }, [selectedPerfil]);

  const handleStartProcess = () => {
    if (selectedPerfil && onTriggerAction) {
      onTriggerAction('processar-perfil', selectedPerfil.nome);
    }
  };

  const handleStopContainers = () => {
    if (onTriggerAction) {
      onTriggerAction('acompanha');
    }
    if (setGlobalProcStatus) {
      setGlobalProcStatus('stopping');
      setTimeout(() => {
        setGlobalProcStatus('stopped');
      }, 2000);
    }
  };

  const handleReset = () => {
    if (setGlobalProcStatus) setGlobalProcStatus('idle');
    if (setGlobalProcProgress) setGlobalProcProgress(0);
    if (setGlobalProcTimer) setGlobalProcTimer(0);
    if (setGlobalProcLogs) setGlobalProcLogs([]);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', width: '100%', animation: 'fadeIn 0.25s ease-out' }}>
      <style dangerouslySetInnerHTML={{ __html: '@keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }' }} />
      
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', marginBottom: '20px', flexWrap: 'wrap', gap: '16px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <h1 style={{ fontSize: '1.75rem', fontWeight: 800, color: '#0f172a', margin: 0, fontFamily: "'Outfit', sans-serif", letterSpacing: '-0.5px' }}>
            Perfil: <span style={{ color: selectedPerfil?.isInterno ? '#7c3aed' : '#2563eb' }}>{selectedPerfil?.nome}</span>
          </h1>
          <p style={{ fontSize: '0.88rem', color: '#64748b', margin: 0 }}>
            Gerencie e edite os tópicos de capacitação e roteiros de automação associados a este perfil de acesso.
          </p>
        </div>

        {/* Right side container with search and new action */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {/* Sistema de Busca Eficiente no Perfil */}
          <div style={{ position: 'relative', width: '240px' }}>
            <input
              id="ecad-perfil-search"
              name="ecad-perfil-search"
              autoComplete="off"
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Pesquisar..."
              style={{
                width: '100%',
                padding: '8px 12px 8px 34px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                background: '#ffffff',
                color: '#1e293b',
                fontSize: '0.78rem',
                outline: 'none',
                transition: 'all 0.2s',
                fontWeight: 500,
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.02)'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#7c3aed';
                e.currentTarget.style.boxShadow = '0 0 0 2px rgba(124, 58, 237, 0.1)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#e2e8f0';
                e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.02)';
              }}
            />
            <Search size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
            {searchQuery && (
              <button 
                onClick={() => setSearchQuery('')}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: '#94a3b8',
                  cursor: 'pointer',
                  padding: 0,
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <X size={14} />
              </button>
            )}
          </div>

          <button
            onClick={() => {
              setTopicoForm({ id: '', perfilId: selectedPerfilId || '', titulo: '' });
              setShowTopicoModal(true);
            }}
            style={{
              background: 'rgba(124, 58, 237, 0.08)',
              border: '1px solid rgba(124, 58, 237, 0.2)',
              borderRadius: '8px',
              padding: '8px 14px',
              fontSize: '0.75rem',
              fontWeight: 700,
              color: '#7c3aed',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              transition: 'all 0.15s ease',
              whiteSpace: 'nowrap'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(124, 58, 237, 0.12)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(124, 58, 237, 0.08)';
            }}
          >
            <Plus size={14} /> Novo Tópico
          </button>
        </div>
      </div>

      {/* Grid Layout: Main Content (Left) + Process Monitor Sidebar (Right) */}
      <div style={{ display: 'flex', gap: '20px', width: '100%', alignItems: 'flex-start' }}>
        
        {/* Coluna da Esquerda: Busca + KPIs + Tópicos */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '20px' }}>

          {!searchQuery ? (
            <>
              {/* KPIs com Escolas no lugar de Perfil, cores sólidas, ícones size=36 brancos */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '10px', width: '100%' }}>
                <KpiCard 
                  icon={<BookOpen size={36} />} 
                  label="Tópicos" 
                  value={selectedPerfil?.topicos.length || 0} 
                  color="#7c3aed" 
                  glowColor="rgba(124, 58, 237, 0.15)" 
                />
                <KpiCard 
                  icon={<Building size={36} />} 
                  label="Roteiros" 
                  value={selectedPerfil?.topicos.reduce((acc, t) => acc + (t.subtopicos?.length || 0), 0) || 0} 
                  color="#4f46e5" 
                  glowColor="rgba(79, 70, 229, 0.15)" 
                />
                <KpiCard 
                  icon={<Building size={36} />} 
                  label="Escolas" 
                  value={escolasCount} 
                  color="#3b82f6" 
                  glowColor="rgba(59, 130, 246, 0.15)" 
                />
                <KpiCard 
                  icon={<Clock size={36} />} 
                  label="Pendentes" 
                  value={metrics.pendentes} 
                  color="#ea580c" 
                  glowColor="rgba(234, 88, 12, 0.15)" 
                />
                <KpiCard 
                  icon={<MessageSquare size={36} />} 
                  label="Feedbacks" 
                  value={metrics.feedbacks} 
                  color="#0284c7" 
                  glowColor="rgba(2, 132, 199, 0.15)" 
                />
                <KpiCard 
                  icon={<Lightbulb size={36} />} 
                  label="Sugestões" 
                  value={metrics.sugestoes} 
                  color="#16a34a" 
                  glowColor="rgba(22, 163, 74, 0.15)" 
                />
              </div>

              <div style={{ width: '100%', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                  <h3 style={{ fontSize: '0.95rem', fontWeight: 800, color: '#0f172a', margin: 0, fontFamily: "'Outfit', sans-serif" }}>
                    Tópicos de Capacitação
                  </h3>
                </div>

                {selectedPerfil?.topicos.length === 0 ? (
                  <div style={{ background: '#ffffff', border: '1px dashed #e2e8f0', borderRadius: '12px', padding: '32px', textAlign: 'center', color: '#64748b', fontSize: '0.85rem' }}>
                    Nenhum tópico cadastrado neste perfil de acesso.
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '16px' }}>
                    {selectedPerfil?.topicos.map((topico, idx) => {
                      const topicRoteiros = topico.subtopicos || [];
                      const roteiroIds = topicRoteiros.map(r => r.id);
                      
                      const isTopicActive = activeRoteiro !== null && roteiroIds.includes(activeRoteiro.id);
                      const hasRoteiros = roteiroIds.length > 0;
                      const isTopicCompleted = hasRoteiros && roteiroIds.every(id => completedRoteiroIds.includes(id));
                      const isTopicQueued = !isTopicActive && !isTopicCompleted && roteiroIds.some(id => queuedRoteiros.some(qr => qr.id === id));
                      const queuePosition = isTopicQueued ? queuedRoteiros.findIndex(qr => roteiroIds.includes(qr.id)) + 1 : -1;
                      
                      return (
                        <div
                          key={topico.id}
                          onClick={() => {
                            setSelectedTopicoId(topico.id);
                            setSelectedSubtopicoId(null);
                          }}
                          style={{
                            background: '#ffffff',
                            border: isTopicActive 
                              ? '1px solid #7c3aed' 
                              : isTopicCompleted 
                                ? '1px solid #10b981' 
                                : '1px solid #e2e8f0',
                            borderRadius: '12px',
                            padding: '20px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease-in-out',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            minHeight: '120px',
                            boxShadow: isTopicActive 
                              ? '0 0 14px rgba(124, 58, 237, 0.25)' 
                              : '0 1px 3px rgba(0,0,0,0.02)',
                            animation: isTopicActive ? 'pulseBorder-anim 1.5s infinite alternate' : 'none'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = isTopicActive ? '#7c3aed' : '#7c3aed';
                            e.currentTarget.style.boxShadow = isTopicActive 
                              ? '0 0 18px rgba(124, 58, 237, 0.35)' 
                              : '0 4px 12px rgba(124, 58, 237, 0.05)';
                            e.currentTarget.style.transform = 'translateY(-2px)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = isTopicActive 
                              ? '#7c3aed' 
                              : isTopicCompleted 
                                ? '#10b981' 
                                : '#e2e8f0';
                            e.currentTarget.style.boxShadow = isTopicActive 
                              ? '0 0 14px rgba(124, 58, 237, 0.25)' 
                              : '0 1px 3px rgba(0,0,0,0.02)';
                            e.currentTarget.style.transform = 'translateY(0)';
                          }}
                        >
                          {/* Pulsing card keyframes */}
                          <style dangerouslySetInnerHTML={{ __html: `
                            @keyframes pulseBorder-anim {
                              from { border-color: #7c3aed; box-shadow: 0 0 4px rgba(124, 58, 237, 0.2); }
                              to { border-color: #a78bfa; box-shadow: 0 0 14px rgba(124, 58, 237, 0.45); }
                            }
                            @keyframes pulseText-anim {
                              from { opacity: 0.6; }
                              to { opacity: 1; }
                            }
                            .pulse-active-badge {
                              animation: pulseText-anim 1s infinite alternate;
                            }
                          `}} />
                          
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px', marginBottom: '8px' }}>
                            <div style={{ fontSize: '0.9rem', fontWeight: 800, color: '#1e293b', lineHeight: 1.3 }}>
                              {idx + 1}. {topico.titulo}
                            </div>
                            
                            {isTopicActive && (
                              <span className="pulse-active-badge" style={{
                                background: 'rgba(124, 58, 237, 0.08)',
                                color: '#7c3aed',
                                border: '1px solid rgba(124, 58, 237, 0.2)',
                                padding: '2px 6px',
                                borderRadius: '6px',
                                fontSize: '0.55rem',
                                fontWeight: 800,
                                whiteSpace: 'nowrap'
                              }}>
                                ● ATIVO
                              </span>
                            )}
                            {isTopicQueued && (
                              <span style={{
                                background: '#eff6ff',
                                color: '#1d4ed8',
                                border: '1px solid #bfdbfe',
                                padding: '2px 6px',
                                borderRadius: '6px',
                                fontSize: '0.55rem',
                                fontWeight: 800,
                                whiteSpace: 'nowrap'
                              }}>
                                ⏳ FILA [{queuePosition}]
                              </span>
                            )}
                            {isTopicCompleted && (
                              <span style={{
                                background: '#f0fdf4',
                                color: '#15803d',
                                border: '1px solid #bbf7d0',
                                padding: '2px 6px',
                                borderRadius: '6px',
                                fontSize: '0.55rem',
                                fontWeight: 800,
                                whiteSpace: 'nowrap'
                              }}>
                                ✅ OK
                              </span>
                            )}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.72rem', color: '#64748b' }}>
                            <span>{topico.subtopicos?.length || 0} roteiro(s)</span>
                            <ChevronRight size={14} style={{ color: '#7c3aed' }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div style={{ width: '100%', background: 'linear-gradient(135deg, rgba(124, 58, 237, 0.05) 0%, rgba(217, 70, 239, 0.01) 100%)', border: '1px solid rgba(124, 58, 237, 0.15)', borderRadius: '12px', padding: '20px', display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                <Bot size={24} style={{ color: '#7c3aed', flexShrink: 0 }} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <h4 style={{ fontSize: '0.88rem', fontWeight: 800, color: '#1e293b', margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
                    Alice Copiloto AI Studio
                  </h4>
                  <p style={{ fontSize: '0.78rem', color: '#475569', margin: 0, lineHeight: 1.5 }}>
                    Este perfil de acesso está selecionado no contexto da IA. Você pode pedir para a Alice sugerir e planejar novos tópicos baseados em necessidades reais ou desenhar novas trilhas de treinamento usando linguagem natural no painel à direita.
                  </p>
                  <div style={{ fontSize: '0.72rem', color: '#64748b', fontStyle: 'italic', marginTop: '4px' }}>
                    Dica: "Quais tópicos de capacitação seriam úteis para o perfil {selectedPerfil?.nome}?"
                  </div>
                </div>
              </div>
            </>
          ) : (
            // Resultados da Busca do Perfil
            (() => {
              const query = searchQuery.trim().toLowerCase();
              const matchedProfileTopics = selectedPerfil?.topicos.filter(t => t.titulo.toLowerCase().includes(query)) || [];
              
              const matchedProfileSubtopics: Array<{ topico: Topico; sub: Subtopico }> = [];
              selectedPerfil?.topicos.forEach(t => {
                (t.subtopicos || []).forEach(s => {
                  if (s.titulo.toLowerCase().includes(query) || (s.descricao && s.descricao.toLowerCase().includes(query))) {
                    matchedProfileSubtopics.push({ topico: t, sub: s });
                  }
                });
              });
              const hasProfileMatches = matchedProfileTopics.length > 0 || matchedProfileSubtopics.length > 0;

              return (
                <div style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: '20px' }}>
                  {matchedProfileTopics.length > 0 && (
                    <div>
                      <h4 style={{ fontSize: '0.82rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', marginBottom: '10px' }}>
                        Tópicos Encontrados ({matchedProfileTopics.length})
                      </h4>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '12px' }}>
                        {matchedProfileTopics.map(t => (
                          <div
                            key={t.id}
                            onClick={() => {
                              setSelectedTopicoId(t.id);
                              setSelectedSubtopicoId(null);
                            }}
                            style={{
                              background: '#ffffff',
                              border: '1px solid #e2e8f0',
                              borderRadius: '10px',
                              padding: '16px',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
                            }}
                            onMouseEnter={e => {
                              e.currentTarget.style.borderColor = '#7c3aed';
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(124, 58, 237, 0.05)';
                            }}
                            onMouseLeave={e => {
                              e.currentTarget.style.borderColor = '#e2e8f0';
                              e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.02)';
                            }}
                          >
                            <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#1e293b' }}>
                              📂 {t.titulo}
                            </span>
                            <ChevronRight size={14} style={{ color: '#64748b' }} />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {matchedProfileSubtopics.length > 0 && (
                    <div>
                      <h4 style={{ fontSize: '0.82rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', marginBottom: '10px' }}>
                        Roteiros Encontrados ({matchedProfileSubtopics.length})
                      </h4>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px' }}>
                        {matchedProfileSubtopics.map(({ topico, sub }) => (
                          <div
                            key={sub.id}
                            onClick={() => {
                              setSelectedTopicoId(topico.id);
                              setSelectedSubtopicoId(sub.id);
                            }}
                            style={{
                              background: '#ffffff',
                              border: '1px solid #e2e8f0',
                              borderRadius: '10px',
                              padding: '16px',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '6px',
                              boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
                            }}
                            onMouseEnter={e => {
                              e.currentTarget.style.borderColor = '#7c3aed';
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(124, 58, 237, 0.05)';
                            }}
                            onMouseLeave={e => {
                              e.currentTarget.style.borderColor = '#e2e8f0';
                              e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.02)';
                            }}
                          >
                            <span style={{ fontSize: '0.62rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                              📂 {topico.titulo}
                            </span>
                            <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#1e293b' }}>
                              📖 {sub.titulo}
                            </span>
                            <span style={{ fontSize: '0.72rem', color: '#64748b', display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {sub.descricao || 'Sem descrição'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {!hasProfileMatches && (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#64748b', fontSize: '0.85rem' }}>
                      Nenhum resultado encontrado para "{searchQuery}".
                    </div>
                  )}
                </div>
              );
            })()
          )}
        </div>

        {/* Coluna da Direita: Painel de Processamento do Perfil */}
        <EcadProcessSidebar
          title="Processamento Perfil"
          subtitle={`Monitore e execute automações de ${selectedPerfil?.nome}`}
          type="perfil"
          name={selectedPerfil?.nome}
          totalRoteiros={selectedPerfil?.topicos.reduce((acc, t) => acc + (t.subtopicos?.length || 0), 0) || 0}
          pendingCount={metrics.pendentes}
          onTriggerAction={onTriggerAction}
          status={globalProcStatus}
          setStatus={setGlobalProcStatus}
          progress={globalProcProgress}
          setProgress={setGlobalProcProgress}
          timer={globalProcTimer}
          setTimer={setGlobalProcTimer}
          logs={globalProcLogs}
          setLogs={setGlobalProcLogs}
          autoShutdown={globalAutoShutdown}
          setAutoShutdown={setGlobalAutoShutdown}
          handleStartProcess={handleStartProcess}
          handleStopContainers={handleStopContainers}
          handleReset={handleReset}
        />

      </div>
    </div>
  );
}
